/**
 * CRYSTALE — Cart checkbox "Save for later" (private version v2)
 *
 * How it works:
 *  - Every item in the cart drawer gets a checkbox (default: checked).
 *  - Unchecking an item REMOVES it from the Shopify cart via AJAX and stashes
 *    it in localStorage. The drawer re-renders showing two groups:
 *      1. items still in cart (will go to checkout)
 *      2. "Saved for later" items (kept visible, not in checkout)
 *  - Re-checking (or "Move to cart") re-adds the item via AJAX and removes it
 *    from localStorage.
 *  - The Shopify cart ALWAYS equals exactly what goes to checkout, so the
 *    checkout button needs no interception. Safe by design.
 *  - Guest-friendly: no login required. Same browser = items persist across
 *    visits via localStorage.
 *
 * Cross-device (v2, experimental):
 *  - Every save-for-later change is ALSO written to a hidden cart attribute
 *    (_crystale_sfl). If the Persistent Cart app syncs the full cart object
 *    (items + attributes) for logged-in customers, the saved list follows.
 *  - On page load, if the cart carries the attribute it becomes the source of
 *    truth (overwrites localStorage). If the cart has no such attribute
 *    (fresh cart, guest, or Persistent Cart didn't sync it), localStorage is
 *    used as before. Safe fallback: worst case = same as v1.
 *  - Caveats: clearing the cart or checking out destroys the attribute
 *    (localStorage copy survives); only works when the customer is logged in
 *    AND Persistent Cart actually syncs attributes (unverified).
 *
 * Trilingual: EN / 繁中 (zh-TW) / 簡中 (zh-CN), detected from <html lang>.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'crystale_sfl_v1';
  var ATTR_KEY = '_crystale_sfl'; // hidden cart attribute (underscore = hidden at checkout)

  var STRINGS = {
    'en': {
      savedTitle: 'Saved for later',
      moveToCart: 'Move to cart',
      remove: 'Remove',
      savedEmpty: '',
      addedBack: 'Added back to cart',
      addFailed: 'Sorry, this item is no longer available and was removed.',
      selectHint: 'Uncheck to save for later'
    },
    'zh-TW': {
      savedTitle: '稍後購買',
      moveToCart: '加回購物車',
      remove: '刪除',
      savedEmpty: '',
      addedBack: '已加回購物車',
      addFailed: '抱歉，此貨品已售罄或下架，已為你刪除。',
      selectHint: '剔除即係遲啲先買'
    },
    'zh-CN': {
      savedTitle: '稍后购买',
      moveToCart: '加回购物车',
      remove: '删除',
      savedEmpty: '',
      addedBack: '已加回购物车',
      addFailed: '抱歉，此商品已售罄或下架，已为你删除。',
      selectHint: '取消勾选即为稍后购买'
    }
  };

  function getLocale() {
    var lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (lang.indexOf('zh-cn') === 0 || lang.indexOf('zh_hans') === 0) return 'zh-CN';
    if (lang.indexOf('zh') === 0) return 'zh-TW'; // zh-TW / zh-HK default to Traditional
    if (lang.indexOf('en') === 0) return 'en';
    return 'zh-TW'; // CRYSTALE primary market: Hong Kong
  }

  function t(key) {
    var l = getLocale();
    return (STRINGS[l] && STRINGS[l][key]) || STRINGS['zh-TW'][key] || key;
  }

  // ---------- storage (guarded: private mode / full quota) ----------
  var storageOK = true;
  function getSaved() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      storageOK = false;
      return [];
    }
  }
  function setSaved(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      storageOK = false; // quota full or disabled: feature degrades silently
    }
    // Best-effort cross-device sync: mirror the list into a hidden cart
    // attribute so the Persistent Cart app can carry it to other devices
    // for logged-in customers. Fire-and-forget; localStorage is the fallback.
    try {
      var compact = items.map(function (it) {
        return {
          key: it.key,
          variantId: it.variantId,
          quantity: it.quantity || 1,
          title: it.title || '',
          variantTitle: it.variantTitle || '',
          image: it.image || '',
          priceFormatted: it.priceFormatted || ''
        };
      });
      var attrs = {};
      attrs[ATTR_KEY] = JSON.stringify(compact);
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () { /* attribute too large etc.: ignore, localStorage covers */ });
    } catch (e) { /* ignore */ }
  }

  // On load: if the (possibly synced) cart carries our attribute, it is the
  // source of truth — adopt it into localStorage. Otherwise keep localStorage.
  function syncFromCartAttributes() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.attributes || !(ATTR_KEY in cart.attributes)) return;
        try {
          var arr = JSON.parse(cart.attributes[ATTR_KEY]);
          if (!Array.isArray(arr)) return;
          var sane = arr.filter(function (it) { return it && it.key && it.variantId; });
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sane));
          renderSavedSection();
        } catch (e) { /* malformed: ignore */ }
      })
      .catch(function () { /* offline etc.: ignore */ });
  }

  // ---------- ajax helpers ----------
  function cartChange(lineKey, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty })
    }).then(function (r) { return r.json(); });
  }
  function cartAdd(variantId, qty, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty, properties: properties || {} })
    }).then(function (r) {
      if (!r.ok) throw new Error('add-failed');
      return r.json();
    });
  }

  function refreshDrawer() {
    // Use the theme's own refresh so subtotal/count stay correct.
    if (window.theme && typeof window.theme.updateCartSummaries === 'function') {
      window.theme.updateCartSummaries(false);
    } else {
      window.location.reload();
    }
  }

  // ---------- save-for-later section rendering ----------
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function renderSavedSection() {
    var containers = document.querySelectorAll('[data-sfl-saved]');
    if (!containers.length) return;
    var items = getSaved();
    containers.forEach(function (box) {
      if (!items.length) {
        box.innerHTML = '';
        box.style.display = 'none';
        return;
      }
      box.style.display = '';
      var html = '<div class="cart-summary__section sfl-saved">'
        + '<h6 class="cart-summary__item-list-header__title">' + esc(t('savedTitle')) + '</h6>';
      items.forEach(function (it) {
        html += '<div class="cart-summary-item sfl-saved-item" data-sfl-key="' + esc(it.key) + '">'
          + '<div class="cart-summary-item__image">'
          + (it.image ? '<img src="' + esc(it.image) + '" alt="' + esc(it.title) + '" loading="lazy"/>' : '')
          + '</div>'
          + '<div class="cart-summary-item__detail">'
          + '<div class="cart-summary-item__title-variant">'
          + '<span class="cart-summary-item__title">' + esc(it.title) + '</span>'
          + (it.variantTitle ? '<div class="cart-summary-item__variant">' + esc(it.variantTitle) + '</div>' : '')
          + '</div>'
          + '<div class="cart-summary-item__price"><span class="cart-summary-item__price-current">' + esc(it.priceFormatted || '') + '</span></div>'
          + '<div class="sfl-saved-actions">'
          + '<button type="button" class="btn plain-text sfl-move-back" data-variant-id="' + esc(it.variantId) + '" data-qty="' + (it.quantity || 1) + '">' + esc(t('moveToCart')) + '</button>'
          + ' <a href="#" class="sfl-remove-saved">' + esc(t('remove')) + '</a>'
          + '</div>'
          + '</div></div>';
      });
      html += '</div>';
      box.innerHTML = html;
    });
  }

  // ---------- checkbox behaviour (event delegation: survives drawer re-render) ----------
  document.addEventListener('change', function (e) {
    var cb = e.target.closest ? e.target.closest('.sfl-checkbox') : null;
    if (!cb || cb.disabled) return;
    if (!storageOK && !cb.checked) { cb.checked = true; return; } // no storage: keep checked

    var row = cb.closest('.cart-summary-item');
    var lineKey = cb.getAttribute('data-line-key');
    if (cb.checked) return; // checking happens via "move to cart" in saved section

    // UNCHECKED: stash then remove from cart
    cb.disabled = true;
    var data = {
      key: 'sfl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      lineKey: lineKey,
      variantId: cb.getAttribute('data-variant-id'),
      quantity: parseInt(cb.getAttribute('data-qty') || '1', 10) || 1,
      title: cb.getAttribute('data-title') || '',
      variantTitle: cb.getAttribute('data-variant-title') || '',
      image: cb.getAttribute('data-image') || '',
      priceFormatted: cb.getAttribute('data-price') || ''
    };
    cartChange(lineKey, 0).then(function () {
      var items = getSaved();
      items.push(data);
      setSaved(items);
      refreshDrawer();
    }).catch(function () {
      cb.disabled = false;
      cb.checked = true; // revert on failure: item stays in cart
    });
  });

  document.addEventListener('click', function (e) {
    // "Move to cart" from saved section
    var mv = e.target.closest ? e.target.closest('.sfl-move-back') : null;
    if (mv) {
      e.preventDefault();
      var item = mv.closest('.sfl-saved-item');
      var key = item && item.getAttribute('data-sfl-key');
      var variantId = mv.getAttribute('data-variant-id');
      var qty = parseInt(mv.getAttribute('data-qty') || '1', 10) || 1;
      mv.disabled = true;
      cartAdd(variantId, qty).then(function () {
        setSaved(getSaved().filter(function (it) { return it.key !== key; }));
        refreshDrawer();
      }).catch(function () {
        // product unavailable: drop it from saved, tell the customer
        setSaved(getSaved().filter(function (it) { return it.key !== key; }));
        refreshDrawer();
        alert(t('addFailed'));
      });
      return;
    }
    // "Remove" from saved section (permanent delete)
    var rm = e.target.closest ? e.target.closest('.sfl-remove-saved') : null;
    if (rm) {
      e.preventDefault();
      var rItem = rm.closest('.sfl-saved-item');
      var rKey = rItem && rItem.getAttribute('data-sfl-key');
      setSaved(getSaved().filter(function (it) { return it.key !== rKey; }));
      renderSavedSection();
    }
  });

  // Re-render saved section whenever the drawer DOM is replaced by the theme.
  var drawerRoot = document.getElementById('cart-summary');
  if (drawerRoot && window.MutationObserver) {
    var mo = new MutationObserver(function () { renderSavedSection(); });
    mo.observe(drawerRoot.parentNode || document.body, { childList: true, subtree: false });
  }
  // Cross-tab sync: another tab changed the saved list
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) renderSavedSection();
  });

  // Hide the whole feature gracefully when storage is unavailable.
  function init() {
    try { window.localStorage.setItem('__sfl_test', '1'); window.localStorage.removeItem('__sfl_test'); }
    catch (e) { storageOK = false; }
    if (!storageOK) {
      document.querySelectorAll('.sfl-checkbox-wrap').forEach(function (el) { el.style.display = 'none'; });
      return;
    }
    // Localise checkbox hints (JS-side i18n, no locale-file edits needed)
    document.querySelectorAll('.sfl-checkbox').forEach(function (cb) {
      cb.setAttribute('title', t('selectHint'));
      cb.setAttribute('aria-label', t('selectHint'));
    });
    renderSavedSection();
    // Pick up save-for-later list synced from another device (if any).
    syncFromCartAttributes();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
